import { ActionFunction, json, type MetaFunction } from '@remix-run/node'
import { Form, useActionData, useNavigation } from '@remix-run/react'
import { getUserByEmail, registerUser } from "~/src/logic/UsersDao";
import { createUserSession } from "~/src/logic/UserSessionLogic";
import { LOGIN_URI_PATH, M_URI_FULL_ROOT_PATH } from "~/src/types/openTypes";
import { sendGAdsConversion } from "~/src/logic/GoogleAdsLogic";
import { Lock, Mail } from "lucide-react";


export const meta: MetaFunction = () => {
  return [
    { title: "Register account - WayPick" },
    {
      name: "description",
      content: "Register WayPick account."
    },
  ];
};

const MIN_PASSWORD_LENGTH = 8

function isPasswordStrong(password: string) {
  return /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^a-zA-Z0-9]/.test(password)
}

export const action: ActionFunction = async ({ request }) => {
  const form = await request.formData()
  const email = String(form.get("email"))
  const password = String(form.get("password"))

  if (!email || !password) {
    return json({ error: "Email and password are required." }, { status: 400 })
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return json({ error: "Password must be at least 8 characters." }, { status: 400 })
  }

  if (!isPasswordStrong(password)) {
    return json({
      error: "Password must include uppercase, lowercase, number, and special character.",
    }, { status: 400 })
  }

  const existingUser = await getUserByEmail(email)
  if (existingUser) {
    return json({ error: "User already exists." }, { status: 400 })
  }

  const user = await registerUser(email, password)
  return createUserSession(user.id.toString(), M_URI_FULL_ROOT_PATH);
}

export default function Register() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[5fr_7fr]">
      {/* Left panel: registration form */}
      <div className="flex items-center justify-center bg-white px-6 py-12 sm:px-8 md:px-12">
        <div className="w-full max-w-md space-y-6 mb-20">
          {/* Mobile greeting */}
          <div className="block md:hidden text-center mb-4">
            <h2 className="text-2xl font-bold">Join WayPick</h2>
            <p className="text-gray-500 text-sm mt-2 leading-relaxed">
              Start building personalized sightseeing routes tailored to your interests.
            </p>
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-semibold">Create Account</h1>
            <p className="text-gray-500 text-sm">Sign up and explore with WayPick</p>
          </div>

          {actionData?.error && (
            <p className="text-red-600 text-sm text-center">{actionData.error}</p>
          )}

          <Form method="post" className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  required
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <Lock className="w-4 h-4" />
                </span>
                <input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  required
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              onClick={() => {
                sendGAdsConversion();
              }}
              disabled={isSubmitting}
              className={`w-full text-white py-2 rounded-md text-sm transition duration-200 ${
                isSubmitting
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Creating..." : "Create Account"}
            </button>
          </Form>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <a href={LOGIN_URI_PATH} className="text-blue-600 hover:underline">
              Log in
            </a>
          </p>
        </div>
      </div>

      {/* Right panel: description only */}
      <div className="hidden md:flex flex-col justify-center bg-blue-600 text-white p-10">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Why WayPick?</h2>
          <p className="text-lg leading-relaxed">
            Plan immersive travel routes with themes like architecture, culture, museums, farms, and wineries.
            Join a community of explorers creating unique itineraries - just for you.
          </p>
        </div>
      </div>
    </div>
  );
}
